import React from "react";
import { Link } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { downloadSectionsSampleCsv, downloadSubjectsSampleCsv } from "../../utils/questionImportSample";

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const isBlankHtml = (html) => !html || !html.replace(/<[^>]*>/g, "").trim();

// "2026-09-22T09:30:00Z" → "2026-09-22T15:00" in the admin's local time, for <input type="datetime-local">
export const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Empty values for the schedule, attempt limit and instructions (create form)
export const EMPTY_EXTRAS = { starts_at: "", ends_at: "", max_attempts: "", instructions_en: "", instructions_hi: "" };

// The same fields from a loaded test (edit form)
export const extrasFrom = (test) => ({
  starts_at: toLocalInput(test.starts_at),
  ends_at: toLocalInput(test.ends_at),
  max_attempts: test.max_attempts ? String(test.max_attempts) : "",
  instructions_en: test.instructions_en || "",
  instructions_hi: test.instructions_hi || "",
});

export const validateExtras = (formData, next) => {
  if (formData.starts_at && formData.ends_at && new Date(formData.ends_at) <= new Date(formData.starts_at)) {
    next.ends_at = "The closing time must be after the opening time.";
  }
  const raw = String(formData.max_attempts ?? "").trim();
  const n = Number(raw);
  if (raw !== "" && (!Number.isInteger(n) || n < 1)) {
    next.max_attempts = "Enter a whole number of at least 1, or leave it empty for no limit.";
  }
};

// Empty fields are sent as null so a schedule, limit or instructions can be cleared
export const extrasPayload = (formData) => ({
  starts_at: formData.starts_at ? new Date(formData.starts_at).toISOString() : null,
  ends_at: formData.ends_at ? new Date(formData.ends_at).toISOString() : null,
  max_attempts: String(formData.max_attempts ?? "").trim() ? parseInt(formData.max_attempts, 10) : null,
  instructions_en: isBlankHtml(formData.instructions_en) ? null : formData.instructions_en,
  instructions_hi: isBlankHtml(formData.instructions_hi) ? null : formData.instructions_hi,
});

// Timing fields shared by create and edit. With section-wise timing off, the whole test needs a duration.
export const validateTiming = (formData, next) => {
  const duration = parseInt(formData.duration_minutes, 10);
  if (!formData.sectional_timing && (!duration || duration < 1)) {
    next.duration_minutes = "Enter a time limit for the whole test.";
  }
};

// Timing on: the test duration is not used, so send null (this also clears one saved while timing was off)
export const timingPayload = (formData) => ({
  sectional_timing: formData.sectional_timing ? 1 : 0,
  duration_minutes: formData.sectional_timing ? null : parseInt(formData.duration_minutes, 10),
});

// Per-field messages from a 422, for the fields this form shows
const FORM_FIELDS = [
  "title",
  "course",
  "visibility",
  "nested_sections",
  "sectional_timing",
  "duration_minutes",
  "starts_at",
  "ends_at",
  "max_attempts",
  "instructions_en",
  "instructions_hi",
];
export const fieldErrorsFrom = (response) =>
  Object.fromEntries(
    Object.entries(response?.errors || {})
      .filter(([field, messages]) => FORM_FIELDS.includes(field) && messages?.length)
      .map(([field, messages]) => [field, Array.isArray(messages) ? messages[0] : messages])
  );

// Shared form body for creating and editing a test series.
function TestSeriesForm({
  formData,
  setFormData,
  errors = {},
  setErrors,
  courses,
  coursesLoading,
  coursesError,
  disabled,
  showVisibility = false,
  currentTotalMinutes, // edit only: the test's total_duration_minutes, to prefill the whole-test duration
  sectionsLink, // edit only: where to fix sections missing a duration
  structureLocked = false, // edit only: the test has sections, so its structure can no longer change
}) {
  const update = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name] && setErrors) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const toggleSectionalTiming = (on) => {
    setFormData((prev) => ({
      ...prev,
      sectional_timing: on,
      // Turning timing off: start from the time the test had before (the sum of its sections)
      duration_minutes:
        !on && String(prev.duration_minutes ?? "").trim() === "" && currentTotalMinutes > 0
          ? String(currentTotalMinutes)
          : prev.duration_minutes,
    }));
    if (setErrors) setErrors((prev) => ({ ...prev, sectional_timing: null, duration_minutes: null }));
  };

  const courseNames = courses.map((c) => c.course_name);
  const hasOrphanCourse = formData.course && !courseNames.includes(formData.course);

  return (
    <>
      <section className="form-section">
        <h3 className="form-section-title">
          <i className="bi bi-info-circle"></i> Basic information
        </h3>

        <div className="form-group">
          <label htmlFor="title">
            Title <span className="required">*</span>
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={(e) => update("title", e.target.value)}
            placeholder="e.g. JEE Mains Mock Test 2025"
            disabled={disabled}
            className={errors.title ? "error" : ""}
            maxLength={255}
          />
          {errors.title && (
            <span className="field-error">
              <i className="bi bi-exclamation-circle"></i> {errors.title}
            </span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="description">
            Description <span className="optional">Optional</span>
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={(e) => update("description", e.target.value)}
            placeholder="What does this test series cover? Who is it for?"
            rows="4"
            disabled={disabled}
          />
        </div>

        <div className="form-group">
          <label htmlFor="course">
            Course <span className="required">*</span>
          </label>
          <select
            id="course"
            name="course"
            value={formData.course}
            onChange={(e) => update("course", e.target.value)}
            disabled={disabled || coursesLoading}
            className={errors.course ? "error" : ""}
          >
            <option value="">{coursesLoading ? "Loading courses…" : "Select a course"}</option>
            {hasOrphanCourse && <option value={formData.course}>{formData.course}</option>}
            {courses.map((course) => (
              <option key={course.id ?? course._id ?? course.course_name} value={course.course_name}>
                {course.course_name}
              </option>
            ))}
          </select>
          {errors.course && (
            <span className="field-error">
              <i className="bi bi-exclamation-circle"></i> {errors.course}
            </span>
          )}
          {coursesError && (
            <span className="field-error">
              <i className="bi bi-exclamation-circle"></i> {coursesError}
            </span>
          )}
        </div>
      </section>

      <section className="form-section">
        <h3 className="form-section-title">
          <i className="bi bi-sliders"></i> Access & behaviour
        </h3>

        <div className="form-group">
          <span className="form-label">
            Test type <span className="required">*</span>
          </span>
          <div className="choice-cards" role="radiogroup">
            {[
              {
                value: "free",
                icon: "bi bi-unlock",
                title: "Free",
                text: "Available to every registered student",
              },
              {
                value: "premium",
                icon: "bi bi-gem",
                title: "Premium",
                text: "Only for students with an active subscription",
              },
            ].map((option) => (
              <label
                key={option.value}
                className={`choice-card ${formData.type === option.value ? "selected" : ""} ${
                  disabled ? "disabled" : ""
                }`}
              >
                <input
                  type="radio"
                  name="type"
                  value={option.value}
                  checked={formData.type === option.value}
                  onChange={() => update("type", option.value)}
                  disabled={disabled}
                />
                <span className="choice-icon">
                  <i className={option.icon}></i>
                </span>
                <span className="choice-text">
                  <strong>{option.title}</strong>
                  <small>{option.text}</small>
                </span>
                <i className="bi bi-check-circle-fill choice-check"></i>
              </label>
            ))}
          </div>
        </div>

        {showVisibility && (
          <div className="form-group">
            <span className="form-label">Visibility</span>
            <div className="segmented">
              {[
                { value: "draft", label: "Draft", icon: "bi bi-pencil-square" },
                { value: "published", label: "Published", icon: "bi bi-broadcast" },
              ].map((v) => (
                <button
                  key={v.value}
                  type="button"
                  className={formData.visibility === v.value ? "active" : ""}
                  onClick={() => update("visibility", v.value)}
                  disabled={disabled}
                >
                  <i className={v.icon}></i> {v.label}
                </button>
              ))}
            </div>
            {errors.visibility ? (
              <span className="field-error">
                <i className="bi bi-exclamation-circle"></i> {errors.visibility}
                {sectionsLink && (
                  <>
                    {" "}
                    <Link to={sectionsLink}>Open sections</Link>
                  </>
                )}
              </span>
            ) : (
              <span className="help-text">
                Draft test series are hidden from students. Publishing needs at least one section, and a question in
                every section.
              </span>
            )}
          </div>
        )}

        <div className="form-group">
          <span className="form-label">Paper structure</span>
          <div className="choice-cards" role="radiogroup">
            {[
              {
                value: false,
                icon: "bi bi-layers",
                title: "Sections",
                text: "One level, e.g. Physics, Chemistry, Maths. Questions go straight into each section.",
              },
              {
                value: true,
                icon: "bi bi-diagram-3",
                title: "Subjects with sections",
                text: "e.g. Physics → Section A, Section B. Questions go into the sections inside each subject.",
              },
            ].map((option) => {
              const locked = disabled || structureLocked;
              return (
                <label
                  key={String(option.value)}
                  className={`choice-card ${Boolean(formData.nested_sections) === option.value ? "selected" : ""} ${
                    locked ? "disabled" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="nested_sections"
                    checked={Boolean(formData.nested_sections) === option.value}
                    onChange={() => update("nested_sections", option.value)}
                    disabled={locked}
                  />
                  <span className="choice-icon">
                    <i className={option.icon}></i>
                  </span>
                  <span className="choice-text">
                    <strong>{option.title}</strong>
                    <small>{option.text}</small>
                  </span>
                  <i className="bi bi-check-circle-fill choice-check"></i>
                </label>
              );
            })}
          </div>
          {errors.nested_sections ? (
            <span className="field-error">
              <i className="bi bi-exclamation-circle"></i> {errors.nested_sections}
            </span>
          ) : (
            <span className="help-text">
              {structureLocked
                ? "The structure can't be changed once the test has sections."
                : "Choose before adding sections: it can't be changed once the test has sections. Results are shown subject-wise and section-wise."}
            </span>
          )}
          {/* Each structure imports from its own CSV format */}
          <span className="help-text">
            <i className="bi bi-file-earmark-spreadsheet"></i> Sample CSV for{" "}
            {formData.nested_sections ? "subjects → sections" : "sections"}:{" "}
            <button
              type="button"
              className="link-button"
              onClick={() =>
                formData.nested_sections
                  ? downloadSubjectsSampleCsv(formData.sectional_timing !== false)
                  : downloadSectionsSampleCsv(formData.sectional_timing !== false)
              }
            >
              {formData.nested_sections ? "subjects_sections_questions_sample.csv" : "sections_questions_sample.csv"}
            </button>
          </span>
        </div>

        <div className="form-group">
          <span className="form-label">Section switching</span>
          <label className="switch-card">
            <span className="choice-text">
              <strong>Let students switch between sections</strong>
              <small>
                When on, students can move freely between sections during the test. When off, they must
                finish sections in order.
              </small>
            </span>
            <span className="toggle-switch">
              <input
                type="checkbox"
                id="section_toggle"
                name="section_toggle"
                checked={formData.section_toggle}
                onChange={(e) => update("section_toggle", e.target.checked)}
                disabled={disabled}
              />
              <span className="toggle-slider"></span>
            </span>
          </label>
        </div>

        <div className="form-group">
          <span className="form-label">Section-wise timing</span>
          <label className="switch-card">
            <span className="choice-text">
              <strong>Time each section separately</strong>
              <small>
                When on, every section has its own timer and duration. When off, the whole test has one timer and
                section durations are optional.
              </small>
            </span>
            <span className="toggle-switch">
              <input
                type="checkbox"
                id="sectional_timing"
                name="sectional_timing"
                checked={formData.sectional_timing}
                onChange={(e) => toggleSectionalTiming(e.target.checked)}
                disabled={disabled}
              />
              <span className="toggle-slider"></span>
            </span>
          </label>
          {errors.sectional_timing && (
            <span className="field-error">
              <i className="bi bi-exclamation-circle"></i> {errors.sectional_timing}
              {sectionsLink && (
                <>
                  {" "}
                  <Link to={sectionsLink}>Set section durations</Link>
                </>
              )}
            </span>
          )}
        </div>

        {!formData.sectional_timing && (
          <div className="form-group">
            <label htmlFor="duration_minutes">
              Test duration (minutes) <span className="required">*</span>
            </label>
            <input
              type="number"
              id="duration_minutes"
              name="duration_minutes"
              value={formData.duration_minutes}
              onChange={(e) => update("duration_minutes", e.target.value)}
              placeholder="e.g. 180"
              min="1"
              step="1"
              disabled={disabled}
              className={errors.duration_minutes ? "error" : ""}
            />
            {errors.duration_minutes ? (
              <span className="field-error">
                <i className="bi bi-exclamation-circle"></i> {errors.duration_minutes}
              </span>
            ) : (
              <span className="help-text">One timer for the whole test.</span>
            )}
          </div>
        )}
      </section>

      <section className="form-section">
        <h3 className="form-section-title">
          <i className="bi bi-calendar-event"></i> Schedule & attempts
        </h3>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="starts_at">
              Opens at <span className="optional">Optional</span>
            </label>
            <input
              type="datetime-local"
              id="starts_at"
              value={formData.starts_at || ""}
              onChange={(e) => update("starts_at", e.target.value)}
              disabled={disabled}
              className={errors.starts_at ? "error" : ""}
            />
            {errors.starts_at ? (
              <span className="field-error">
                <i className="bi bi-exclamation-circle"></i> {errors.starts_at}
              </span>
            ) : (
              <span className="help-text">Students can't start the test before this time.</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="ends_at">
              Closes at <span className="optional">Optional</span>
            </label>
            <input
              type="datetime-local"
              id="ends_at"
              value={formData.ends_at || ""}
              min={formData.starts_at || undefined}
              onChange={(e) => update("ends_at", e.target.value)}
              disabled={disabled}
              className={errors.ends_at ? "error" : ""}
            />
            {errors.ends_at ? (
              <span className="field-error">
                <i className="bi bi-exclamation-circle"></i> {errors.ends_at}
              </span>
            ) : (
              <span className="help-text">No new attempts after this time.</span>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="max_attempts">
            Attempts allowed per student <span className="optional">Optional</span>
          </label>
          <input
            type="number"
            id="max_attempts"
            value={formData.max_attempts ?? ""}
            onChange={(e) => update("max_attempts", e.target.value)}
            placeholder="No limit"
            min="1"
            step="1"
            disabled={disabled}
            className={errors.max_attempts ? "error" : ""}
          />
          {errors.max_attempts ? (
            <span className="field-error">
              <i className="bi bi-exclamation-circle"></i> {errors.max_attempts}
            </span>
          ) : (
            <span className="help-text">Counts submitted attempts. Leave empty for unlimited attempts.</span>
          )}
        </div>
      </section>

      <section className="form-section">
        <h3 className="form-section-title">
          <i className="bi bi-card-text"></i> Instructions
        </h3>
        <p className="help-text form-section-intro">
          Shown to students before they start the test. Each section can also have its own instructions.
        </p>

        <div className="form-group">
          <label>
            Instructions (English) <span className="optional">Optional</span>
          </label>
          <ReactQuill
            theme="snow"
            value={formData.instructions_en || ""}
            onChange={(value) => update("instructions_en", value)}
            placeholder="e.g. Each question carries 4 marks; 1 mark is deducted for a wrong answer…"
            className="rich-editor"
            modules={QUILL_MODULES}
            readOnly={disabled}
          />
        </div>

        <div className="form-group">
          <label>
            Instructions (Hindi) <span className="optional">Optional</span>
          </label>
          <ReactQuill
            theme="snow"
            value={formData.instructions_hi || ""}
            onChange={(value) => update("instructions_hi", value)}
            placeholder="निर्देश…"
            className="rich-editor"
            modules={QUILL_MODULES}
            readOnly={disabled}
          />
        </div>
      </section>
    </>
  );
}

export default TestSeriesForm;
